import {
  Button,
  Checkbox,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import { setNestedObjectValues } from 'formik';
import { omit, uniq } from 'ramda';
import { FormikActions } from 'components/Form';
import PourPresetGroupField from 'components/PourPresetGroupField';
import ProgressButton from 'components/ProgressButton';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import useForm from 'hooks/useForm';
import { LocalDate } from '@js-joda/core';
import {
  ModelNodeDataForm,
  PipeId,
  Point3D,
  ProjectModule,
  StopePipeArray,
  UpdateModelNodeDataForm
} from 'providers/api';
import {
  formatUserFacingApiErrorMessage,
  getStopeUpdateBlockedByExistingPoursMessage,
  isStopeUpdateBlockedByExistingPoursError
} from 'providers/api/apiErrorMessages';
import {
  SingleNodeMutation,
  useUpdateNodeMutation
} from 'providers/api/useMineModels';
import React, { useEffect } from 'react';
import { sanitiseNumber, dayJsToLocalDate, LocalDateToDayJs } from 'utils';
import { pumpPressureBarToKpa, pumpPressureKpaToBar } from 'utils/unitConversions';
import useRoles from 'hooks/useRoles';
import { isAdminUser, isPcUser } from 'utils/roles';
import {
  SchemaOf,
  array,
  boolean,
  number,
  object,
  string
} from 'yup';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import ProjectAuthorise from 'modules/project/ProjectAuthorise';
import { Permission } from 'providers/authorisation';
import { enqueueSnackbar } from 'notistack';
import UnitSystemNumberField from '../StopePanelPage/RecipesTab/RecipeForm/UnitSystemNumberField';
import StopeTitleCard from '../StopePanelPage/StopeTitleCard';
import PipeTypeSelectField from './PipeTypeSelectField';
import { Model } from '../hooks/useModelState';
import { PCStope } from '../MineModel/Stopes';

const pointSchema: SchemaOf<Point3D> = object().shape({
  x: number().required('nodeX is required'),
  y: number().required('nodeX is required'),
  z: number().required('nodeX is required'),
});

const normalisePipeArray = (row: Partial<StopePipeArray> | undefined): StopePipeArray => ({
  pipeId: (row?.pipeId && typeof row.pipeId === 'object'
    ? row.pipeId
    : { value: (row as any)?.pipeId ?? '' }) as PipeId,
  pipeVerticalLength: row?.pipeVerticalLength ?? 0,
  pipeHorizontalLength: row?.pipeHorizontalLength ?? 0,
});

const pipeArrayKey = (pipeArray: StopePipeArray) => {
  const normalizedPipeArray = normalisePipeArray(pipeArray);

  return [
    normalizedPipeArray.pipeId?.value ?? '',
    Number(normalizedPipeArray.pipeHorizontalLength ?? 0),
    Number(normalizedPipeArray.pipeVerticalLength ?? 0),
  ].join('|');
};

const arePipeArraysEquivalent = (currentPipeArrays?: StopePipeArray[], nextPipeArrays?: StopePipeArray[]) => {
  const currentKeys = (currentPipeArrays ?? []).map(pipeArrayKey).sort();
  const nextKeys = (nextPipeArrays ?? []).map(pipeArrayKey).sort();

  return currentKeys.length === nextKeys.length
    && currentKeys.every((key, index) => key === nextKeys[index]);
};

interface EditableStopePourGuard {
  volume?: number | null;
  pipeArrays?: StopePipeArray[] | null;
  stopeOverview?: {
    totalCount?: number | null;
    plannedCount?: number | null;
    completedCount?: number | null;
    nextPourId?: string | null;
    nextPourDate?: unknown;
  } | null;
}

const stopeHasExistingPours = (stope?: EditableStopePourGuard | null) => {
  const overview = stope?.stopeOverview;

  return Number(overview?.totalCount ?? 0) > 0
    || Number(overview?.plannedCount ?? 0) > 0
    || Number(overview?.completedCount ?? 0) > 0
    || Boolean(overview?.nextPourId)
    || Boolean(overview?.nextPourDate);
};

const stopePhysicalDetailsChanged = (
  currentStope?: EditableStopePourGuard | null,
  nextStope?: EditableStopePourGuard | null,
) => {
  if (!currentStope || !nextStope) return false;

  return Number(currentStope.volume ?? 0) !== Number(nextStope.volume ?? 0)
    || !arePipeArraysEquivalent(currentStope.pipeArrays ?? undefined, nextStope.pipeArrays ?? undefined);
};

const createStopePipeArraySchema = (isPatersonAndCooke: boolean): SchemaOf<StopePipeArray> => object({
  pipeId: object({
    value: string(),
  })
    .when([], {
      is: () => !isPatersonAndCooke,
      then: (schema) => schema.required(i18n._('Pipe Type is required')),
      otherwise: (schema) => schema.notRequired(),
    })
    .default({ value: '' }),

  pipeVerticalLength: number()
    .min(0, i18n._('Pipe Vertical Length must be >= 0'))
    .notRequired()
    .default(0),

  pipeHorizontalLength: number()
    .min(0, i18n._('Pipe Horizontal Length must be >= 0'))
    .notRequired()
    .default(0),
});

const createModelNodeDataSchema = (isPatersonAndCooke: boolean): SchemaOf<ModelNodeDataForm> => {
  const stopePipeArraySchema = createStopePipeArraySchema(isPatersonAndCooke);

  return object().shape({
    nodeId: string().required(),
    nodeName: string().notRequired(),
    pipeId: string().default('').required('Pipe type is required'),
    isStope: boolean(),
    isPit: boolean(),
    level: string().notRequired(),
    area: string().notRequired(),
    isPlantNode: boolean(),
    boosterPressure: number().min(0).notRequired(),
    ruptureDiskPressure: number().min(0).notRequired(),
    chokeHead: number()
      .min(0, i18n._('Choke Station Head must be greater than or equal to 0'))
      .notRequired()
      .test(
        'choke-head-excludes-booster',
        i18n._('A node cannot have both a Booster Pump Pressure and a Choke Station Head'),
        (value, context) => {
          const { boosterPressure } = context.parent as { boosterPressure?: number };
          return !((value ?? 0) > 0 && (boosterPressure ?? 0) > 0);
        },
      ),
    pit: string().notRequired(),
    stopeData: array()
      .of(
        object().shape({
          stopeId: string().notRequired(),
          stopeName: string()
            .default('')
            .when([], {
              is: () => !isPatersonAndCooke,
              then: (schema) => schema.required(i18n._('Stope name is required')),
              otherwise: (schema) => schema.notRequired(),
            }),
          volume: number()
            .min(1, i18n._('Stope Volume must be greater than or equal to 1'))
            .when([], {
              is: () => !isPatersonAndCooke,
              then: (schema) => schema.required(i18n._('Stope Volume is required')),
              otherwise: (schema) => schema.notRequired().default(1000),
            }),
          level: string().notRequired(),
          area: string().notRequired(),
          pourPresetGroupId: string().notRequired(),
          pipeArrays: array()
            .of(stopePipeArraySchema)
            .default([]),
        }),
      )
      .notRequired()
      .default([]),
    point: pointSchema.required(),
  });
};

const createNodeDataSchema = (isPatersonAndCooke: boolean): SchemaOf<UpdateModelNodeDataForm> => {
  const modelNodeDataSchema = createModelNodeDataSchema(isPatersonAndCooke);
  return object().shape({
    nodeId: string().default(''),
    modelNodeDataForm: modelNodeDataSchema,
  });
};

const createSingleNodeMutation = (isPatersonAndCooke: boolean): SchemaOf<SingleNodeMutation> => {
  const nodeDataSchema = createNodeDataSchema(isPatersonAndCooke);
  return object().shape({
    projectName: string().required(),
    nodeId: string().required(),
    data: nodeDataSchema,
  });
};

const defaultPipeArrayRow: StopePipeArray = {
  pipeId: { value: '' } as PipeId,
  pipeHorizontalLength: 0,
  pipeVerticalLength: 0,
};

interface NodeFormProps {
  nodeId: string;
  model: Model;
  addStopeMode?: boolean;
  onComplete: (shouldRefresh?: boolean) => void;
}

const NodeForm = ({ nodeId, model, addStopeMode, onComplete }: NodeFormProps) => {
  const roles = useRoles();
  const isAdmin = isAdminUser(roles) && !isPcUser(roles);
  const isPatersonAndCooke = isPcUser(roles);
  const canEditCoordinates = isAdmin || isPatersonAndCooke;

  const getNextStopeName = React.useCallback(() => {
    const allStopes = Object.values(model.stopes ?? {}).flat() as PCStope[];
    const stopeNumbers = allStopes
      .map((s) => {
        const match = s.data.stopeName?.match(/^Stope\s+(\d+)$/i);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((n) => n > 0);
    const nextNumber = stopeNumbers.length > 0 ? Math.max(...stopeNumbers) + 1 : 1;
    return `Stope ${nextNumber}`;
  }, [model.stopes]);

  const getFirstPipeId = React.useCallback(() => {
    const pipeIds = Object.keys(model.pipes ?? {});
    return pipeIds.length > 0 ? pipeIds[0] : '';
  }, [model.pipes]);

  const buildCleanStopeData = (stopeList: any[]) => (Array.isArray(stopeList) ? stopeList : []).map(
    (s) => {
      const firstPipeId = getFirstPipeId();
      let normalizedPipeArrays: StopePipeArray[] = [];
      if (Array.isArray(s.pipeArrays) && s.pipeArrays.length > 0) {
        normalizedPipeArrays = s.pipeArrays.map((pa: StopePipeArray) => {
          const normalized = normalisePipeArray(pa);
          if (isPatersonAndCooke) {
            if (!normalized.pipeId?.value || normalized.pipeId.value === '') {
              normalized.pipeId = { value: firstPipeId } as PipeId;
            }
            if (normalized.pipeHorizontalLength === undefined || normalized.pipeHorizontalLength === null) {
              normalized.pipeHorizontalLength = 0;
            }
            if (normalized.pipeVerticalLength === undefined || normalized.pipeVerticalLength === null) {
              normalized.pipeVerticalLength = 0;
            }
          }
          return normalized;
        });
      } else if (isPatersonAndCooke) {
        normalizedPipeArrays = [{
          pipeId: { value: firstPipeId || '' } as PipeId,
          pipeHorizontalLength: 0,
          pipeVerticalLength: 0,
        }];
      }

      const stopeName = isPatersonAndCooke && (!s.stopeName || s.stopeName.trim() === '')
        ? getNextStopeName()
        : (s.stopeName || '');
      const stopeVolume = isPatersonAndCooke && (!s.volume || s.volume === 0)
        ? 1000
        : (s.volume ?? 0);

      return {
        stopeId: s.stopeId ?? undefined,
        stopeName,
        volume: stopeVolume,
        level: s.level ?? undefined,
        area: s.area ?? undefined,
        pourPresetGroupId: s.pourPresetGroupId ?? undefined,
        startDate: s.startDate ?? undefined,
        endDate: s.endDate ?? undefined,
        pipeArrays: normalizedPipeArrays,
      };
    },
  );

  const selectedStopeId = model.selectedStope?.originNode?.id === nodeId
    ? model.selectedStope.data.stopeId
    : undefined;
  const stopesData = model.stopes[nodeId] ?? [];

  const normalizedStopes = Array.isArray(stopesData)
    ? stopesData.map((s: PCStope) => {
      const d = s.data;
      const ov = model.mineOverview?.find(
        (sum) => sum.stopeIdentifier.stopeId === d.stopeId,
      ) ?? null;

      return {
        stopeId: d.stopeId,
        stopeName: d.stopeName,
        volume: d.volume,
        pipeArrays: Array.isArray(d.pipeArrays)
          ? d.pipeArrays.map((pa) => normalisePipeArray(pa))
          : [],
        level: d.level,
        area: d.area,
        pourPresetGroupId: d.pourPresetGroupId,
        startDate: d.startDate,
        endDate: d.endDate,
        stopeOverview: ov,
        stopeGeometryFile: (d as any).stopeGeometryFile,
      };
    })
    : [];

  const nodeData = model.nodes[nodeId];
  const mutation = useUpdateNodeMutation(model.project.entityId);
  const [stopeDataList, setStopeDataList] = React.useState<any[]>(normalizedStopes);
  const [selectedStope, setSelectedStope] = React.useState<any>(
    normalizedStopes.find((m) => m.stopeId === selectedStopeId) ?? null,
  );
  const [isNewStope, setIsNewStope] = React.useState<boolean>(!!addStopeMode);
  const [activeTab, setActiveTab] = React.useState<number>(addStopeMode || selectedStopeId ? 1 : 0);

  const overview = normalizedStopes.length > 0
    ? model.mineOverview.filter((data) => normalizedStopes.some((dt) => dt.stopeId === data.stopeIdentifier.stopeId))
    : [];

  const initialIsStope = Array.isArray(model.stopes?.[nodeId]) && model.stopes[nodeId].length > 0;
  const initialStopeData = initialIsStope
    ? [
      {
        stopeId: selectedStope?.stopeId,
        stopeName: selectedStope?.stopeName,
        volume: selectedStope?.volume,
        level: selectedStope?.level,
        area: selectedStope?.area,
        pourPresetGroupId: selectedStope?.pourPresetGroupId,
        startDate: selectedStope?.startDate,
        endDate: selectedStope?.endDate,
        stopeOverview: overview?.find((m) => m.stopeIdentifier.stopeId === selectedStope?.stopeId),
        pipeArrays:
          selectedStope && Array.isArray(selectedStope.pipeArrays)
            && selectedStope.pipeArrays.length > 0
            ? selectedStope.pipeArrays.map((pa: StopePipeArray) => normalisePipeArray(pa))
            : [defaultPipeArrayRow],
      },
    ]
    : [];

  const { formik, helpers } = useForm({
    mutation,
    formikConfig: {
      initialValues: {
        projectName: model.project.name,
        nodeId,
        data: {
          nodeId,
          modelNodeDataForm: {
            nodeId,
            // Pipes belong to spools now, so the node's own pipe is no longer shown; it only has to stay valid for the API.
            pipeId: nodeData?.pipeId || getFirstPipeId(),
            nodeName: nodeData?.nodeName ?? nodeId,
            pit: nodeData?.pit,
            area: nodeData?.area,
            level: nodeData?.level,
            isStope: initialIsStope,
            isPit: nodeData?.isPit,
            isPlantNode: nodeData?.isPlantNode ?? false,
            boosterPressure: nodeData?.boosterPressure ?? 0,
            ruptureDiskPressure: nodeData?.ruptureDiskPressure ?? 0,
            chokeHead: nodeData?.chokeHead ?? 0,
            point: nodeData?.coords,
            stopeData: initialStopeData,
          },
        },
      },
      // Saving goes through saveNode instead. Formik's submit refuses to run while any schema error stands, including
      // errors on the hidden stope row and a stale server error that only clears when its exact field changes.
      onSubmit: () => undefined,
      validationSchema: createSingleNodeMutation(isPatersonAndCooke),
    },
  });

  const {
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    resetForm,
    isSubmitting,
  } = formik;

  const handleCloseModal = () => {
    resetForm();
    onComplete(false);
  };

  const handleDelete = (deletedNodeId: string, deletedStopeId: string) => {
    model.unsetNodeAsStope(deletedNodeId);
    const foundStopeData = stopeDataList?.find((m) => m.stopeId === deletedStopeId);
    if (foundStopeData) {
      const newStopeDataList = stopeDataList?.filter((m) => m.stopeId !== deletedStopeId);
      if (newStopeDataList?.length === 0) {
        setFieldValue('data.modelNodeDataForm.isStope', false);
      }
      setStopeDataList(newStopeDataList);
    }
  };

  const handleEdit = (editedStopeId: string) => {
    const foundStopeData = stopeDataList?.find((m) => m.stopeId === editedStopeId);
    if (foundStopeData) {
      setSelectedStope(foundStopeData);
      setFieldValue('data.modelNodeDataForm.stopeData[0].stopeId', foundStopeData.stopeId);
      setFieldValue('data.modelNodeDataForm.stopeData[0].stopeName', foundStopeData.stopeName);
      setFieldValue('data.modelNodeDataForm.stopeData[0].volume', foundStopeData.volume);
      setFieldValue('data.modelNodeDataForm.stopeData[0].startDate', foundStopeData.startDate);
      setFieldValue('data.modelNodeDataForm.stopeData[0].endDate', foundStopeData.endDate);
      setFieldValue(
        'data.modelNodeDataForm.stopeData[0].pipeArrays',
        Array.isArray(foundStopeData.pipeArrays)
          && foundStopeData.pipeArrays.length > 0
          ? foundStopeData.pipeArrays.map((pa: StopePipeArray) => normalisePipeArray(pa))
          : [defaultPipeArrayRow],
      );
    }
  };

  const hasStopeData = stopesData?.length > 0;

  const removeStopeData = async (isStope: boolean) => {
    if (!isStope) {
      setStopeDataList([]);
      setFieldValue('data.modelNodeDataForm.isStope', false);
      setFieldValue('data.modelNodeDataForm.stopeData', []);
      setIsNewStope(false);
      setSelectedStope(null);
      return;
    }

    const newIsStope = !values.data.modelNodeDataForm.isStope;

    if (newIsStope && isPatersonAndCooke) {
      const defaultStopeName = getNextStopeName();
      const firstPipeId = getFirstPipeId();
      const defaultPipeArray: StopePipeArray = {
        pipeId: { value: firstPipeId } as PipeId,
        pipeHorizontalLength: 0,
        pipeVerticalLength: 0,
      };

      setFieldValue('data.modelNodeDataForm.isStope', true);
      setFieldValue('data.modelNodeDataForm.stopeData', [
        {
          stopeId: undefined,
          stopeName: defaultStopeName,
          volume: 1000,
          level: undefined,
          area: undefined,
          pourPresetGroupId: undefined,
          pipeArrays: [defaultPipeArray],
        },
      ]);

      const payload = {
        projectName: model.project.name || '',
        data: {
          nodeId,
              modelNodeDataForm: {
                nodeId: values.data.nodeId,
                nodeName: values.data.modelNodeDataForm.nodeName ?? values.data.nodeId,
            pipeId: values.data.modelNodeDataForm.pipeId,
            pit: values.data.modelNodeDataForm.pit,
            isStope: true,
            isPit: values.data.modelNodeDataForm.isPit,
            level: values.data.modelNodeDataForm.level,
            area: values.data.modelNodeDataForm.area,
            isPlantNode: values.data.modelNodeDataForm.isPlantNode,
            boosterPressure: values.data.modelNodeDataForm.boosterPressure ?? 0,
            ruptureDiskPressure: values.data.modelNodeDataForm.ruptureDiskPressure ?? 0,
            chokeHead: values.data.modelNodeDataForm.chokeHead ?? 0,
            point: {
              x: values.data.modelNodeDataForm.point?.x,
              y: values.data.modelNodeDataForm.point?.y,
              z: values.data.modelNodeDataForm.point?.z,
            },
            stopeData: [
              {
                stopeId: undefined,
                stopeName: defaultStopeName,
                volume: 1000,
                level: undefined,
                area: undefined,
                pourPresetGroupId: undefined,
                pipeArrays: [defaultPipeArray],
              },
            ],
          },
        },
      };

      try {
        await mutation.mutateAsync(payload as any);
        onComplete(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
      return;
    }

    if (isStope) {
      setFieldValue('data.modelNodeDataForm.isStope', newIsStope);
      setIsNewStope(newIsStope);

      if (newIsStope) {
        setFieldValue('data.modelNodeDataForm.stopeData', [
          {
            stopeId: undefined,
            stopeName: '',
            volume: 0,
            level: undefined,
            area: undefined,
            pourPresetGroupId: undefined,
            pipeArrays: [defaultPipeArrayRow],
          },
        ]);
      } else {
        setFieldValue('data.modelNodeDataForm.stopeData', []);
        setSelectedStope(null);
      }
    }
  };

  const formatErrorMessage = (msg: string): string => {
    if (!msg) return msg;
    if (msg.includes('pipeId') && msg.includes('string')) {
      return i18n._('Pipe Type is required');
    }

    if (msg.toLowerCase().includes('volume')) {
      return i18n._('Stope Volume is required');
    }

    return msg;
  };

  const flattenErrors = (err: any): string[] => {
    if (!err) return [];

    const messages: string[] = [];

    const recurse = (obj: any) => {
      if (!obj) return;

      if (typeof obj === 'string') {
        messages.push(formatErrorMessage(obj));
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach(recurse);
        return;
      }

      if (typeof obj === 'object') {
        Object.values(obj).forEach(recurse);
      }
    };

    recurse(err);
    return messages;
  };

  const stopeFormIsOpen = isNewStope || Boolean(selectedStope);

  const mergeStopeFormIntoList = () => {
    const list = [...(stopeDataList ?? [])];
    const formRow = values?.data?.modelNodeDataForm?.stopeData?.[0] ?? {};
    const currentStopeId: string = selectedStope?.stopeId ?? formRow.stopeId ?? `__tmp_${Date.now()}`;
    const foundOverview = model.mineOverview?.find((summary) => summary?.stopeIdentifier?.stopeId === currentStopeId) ?? null;
    const index = list.findIndex((s) => s?.stopeId === currentStopeId);

    let pipeArrays = Array.isArray(formRow.pipeArrays) && formRow.pipeArrays.length > 0
      ? formRow.pipeArrays.map((pa: StopePipeArray) => normalisePipeArray(pa))
      : [defaultPipeArrayRow];

    if (isPatersonAndCooke) {
      pipeArrays = pipeArrays.map((pa) => {
        const normalized = normalisePipeArray(pa);
        if (!normalized.pipeId?.value || normalized.pipeId.value === '') {
          normalized.pipeId = { value: getFirstPipeId() } as PipeId;
        }
        return normalized;
      });

      if (pipeArrays.length === 0) {
        pipeArrays = [{
          pipeId: { value: getFirstPipeId() } as PipeId,
          pipeHorizontalLength: 0,
          pipeVerticalLength: 0,
        }];
      }
    }

    const stopeName = isPatersonAndCooke && (!formRow.stopeName || formRow.stopeName.trim() === '')
      ? getNextStopeName()
      : (formRow.stopeName ?? '-');
    const stopeVolume = isPatersonAndCooke && (!formRow.volume || formRow.volume === 0)
      ? 1000
      : (formRow.volume ?? 0);

    const stope = {
      stopeId: currentStopeId,
      stopeName,
      volume: stopeVolume,
      pipeArrays,
      level: formRow.level ?? undefined,
      area: formRow.area ?? undefined,
      pourPresetGroupId: formRow.pourPresetGroupId ?? undefined,
      startDate: formRow.startDate ?? undefined,
      endDate: formRow.endDate ?? undefined,
      stopeOverview: foundOverview,
      stopeGeometryFile: list[index]?.stopeGeometryFile,
    };
    const currentStope = selectedStope
      ? { ...selectedStope, stopeOverview: selectedStope.stopeOverview ?? foundOverview }
      : selectedStope;
    const showExistingPoursError = !isNewStope
      && stopeHasExistingPours(currentStope)
      && stopePhysicalDetailsChanged(currentStope, stope);

    if (index !== -1) {
      list[index] = stope;
    } else {
      list.push(stope);
    }

    return { stopes: list, showExistingPoursError };
  };

  const saveNode = async () => {
    const errors = await formik.validateForm();
    const formErrors = errors.data?.modelNodeDataForm ?? {};
    // Paterson & Cooke stopes are filled with defaults, and a closed stope form's row is never saved.
    const blockingErrors = stopeFormIsOpen && !isPatersonAndCooke
      ? formErrors
      : omit(['stopeData'], formErrors);
    const messages = uniq(flattenErrors(blockingErrors));

    if (messages.length > 0) {
      enqueueSnackbar(messages.join('\n'), { variant: 'error', autoHideDuration: 4000 });
      formik.setTouched(setNestedObjectValues({ data: { modelNodeDataForm: blockingErrors } }, true), false);
      return;
    }

    const { stopes, showExistingPoursError } = stopeFormIsOpen
      ? mergeStopeFormIntoList()
      : { stopes: stopeDataList ?? [], showExistingPoursError: false };
    const isStope = stopeFormIsOpen || Boolean(values.data.modelNodeDataForm.isStope);

    const payload = {
      projectName: model.project.name || '',
      data: {
        nodeId,
        modelNodeDataForm: {
          nodeId: values.data.nodeId,
          nodeName: values.data.modelNodeDataForm.nodeName ?? values.data.nodeId,
          pipeId: values.data.modelNodeDataForm.pipeId,
          pit: values.data.modelNodeDataForm.pit,
          isStope,
          isPit: values.data.modelNodeDataForm.isPit,
          level: values.data.modelNodeDataForm.level,
          area: values.data.modelNodeDataForm.area,
          isPlantNode: values.data.modelNodeDataForm.isPlantNode,
          boosterPressure: values.data.modelNodeDataForm.boosterPressure ?? 0,
          ruptureDiskPressure: values.data.modelNodeDataForm.ruptureDiskPressure ?? 0,
          chokeHead: values.data.modelNodeDataForm.chokeHead ?? 0,
          point: {
            x: values.data.modelNodeDataForm.point?.x,
            y: values.data.modelNodeDataForm.point?.y,
            z: values.data.modelNodeDataForm.point?.z,
          },
          stopeData: isStope ? buildCleanStopeData(stopes) : [],
        },
      },
    };

    try {
      formik.setSubmitting(true);
      await mutation.mutateAsync(payload as any);
      onComplete(true);
    } catch (error) {
      // The query client already reports every failed mutation; this only explains a failure it can't recognise.
      if (showExistingPoursError && !isStopeUpdateBlockedByExistingPoursError(error)) {
        enqueueSnackbar(formatUserFacingApiErrorMessage(getStopeUpdateBlockedByExistingPoursMessage()), {
          variant: 'error',
          autoHideDuration: 8000,
        });
      }
    } finally {
      formik.setSubmitting(false);
    }
  };

  const submitBtn: [string, JSX.Element] = ['submit', <Trans id="Submit">Submit</Trans>];
  const submitButton = [
    submitBtn,
    (
      <ProgressButton
        variant="contained"
        color="secondary"
        type="submit"
        loading={isSubmitting}
      >
        <Trans id="Submit">Submit</Trans>
      </ProgressButton>
    ),
  ];

  useEffect(() => {
    const hasPitName = !!(values.data.modelNodeDataForm.pit && values.data.modelNodeDataForm.pit.trim() !== '');
    if (hasPitName !== values.data.modelNodeDataForm.isPit) {
      setFieldValue('data.modelNodeDataForm.isPit', hasPitName);
      if (!hasPitName) {
        removeStopeData(false);
      }
    }
  }, [
    values.data.modelNodeDataForm.pit,
    values.data.modelNodeDataForm.isPit,
    setFieldValue,
  ]);

  useEffect(() => {
    if (addStopeMode) {
      setSelectedStope(null);
      setIsNewStope(true);
      setFieldValue('data.modelNodeDataForm.isStope', true);
      setFieldValue('data.modelNodeDataForm.stopeData', [
        {
          stopeId: undefined,
          stopeName: '',
          volume: 0,
          level: undefined,
          area: undefined,
          pourPresetGroupId: undefined,
          pipeArrays: [defaultPipeArrayRow],
        },
      ]);
    }
  }, [addStopeMode]);

  const markFieldTouched = (field: string) => {
    formik.setTouched({
      ...formik.touched,
      [field]: true,
    });
  };

  const currentPipeArrays: StopePipeArray[] = values.data.modelNodeDataForm.stopeData?.[0]?.pipeArrays ?? [defaultPipeArrayRow];

  const handleAddPipeRow = () => {
    const next = [
      ...currentPipeArrays.map((p) => normalisePipeArray(p)),
      { ...defaultPipeArrayRow },
    ];
    setFieldValue('data.modelNodeDataForm.stopeData[0].pipeArrays', next);
  };

  const handleRemovePipeRow = (index: number) => {
    if (currentPipeArrays.length <= 1) return;
    const next = currentPipeArrays
      .map((p) => normalisePipeArray(p))
      .filter((_, i) => i !== index);
    setFieldValue('data.modelNodeDataForm.stopeData[0].pipeArrays', next);
  };

  return (
    <Dialog
      title={i18n._('Add Node')}
      open={!!nodeId}
      onClose={handleCloseModal}
    >
      <DialogTitle id="simple-dialog-title">
        <Trans id="Node Edit">Node Edit</Trans>
      </DialogTitle>
      <Box sx={{ width: 500, m: 2 }}>
        {/* noValidate: the yup schema owns validation; zero-valued number fields render blank and would trip the browser's native "required" */}
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            saveNode();
          }}
        >
          <Paper square sx={{ mb: 2 }}>
            <Tabs
              variant="fullWidth"
              value={activeTab}
              indicatorColor="secondary"
              textColor="secondary"
              onChange={(_e, newValue) => setActiveTab(newValue)}
              aria-label="node edit tabs"
            >
              <Tab label={i18n._('Node')} />
              <Tab label={i18n._('Stopes')} />
            </Tabs>
          </Paper>
          {activeTab === 0 && (
          <>
            <ProjectAuthorise permission={Permission.ManageNodeData}>
              <Box>
                <TextField
                  color="secondary"
                  fullWidth
                  id="data.modelNodeDataForm.nodeName"
                  name="data.modelNodeDataForm.nodeName"
                  label={i18n._('Node Name')}
                  value={values.data.modelNodeDataForm.nodeName ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={helpers.hasError('data.modelNodeDataForm.nodeName')}
                  helperText={helpers.getErrorHelpText('data.modelNodeDataForm.nodeName')}
                />
              </Box>
            </ProjectAuthorise>
            {canEditCoordinates ? (
              <>
                <Box sx={{ pt: 2 }}>
                  <TextField
                    color="secondary"
                    fullWidth
                    type="number"
                    inputMode="decimal"
                    id="data.modelNodeDataForm.point.x"
                    name="data.modelNodeDataForm.point.x"
                    label={i18n._('X Coordinate')}
                    value={values.data.modelNodeDataForm.point.x ?? ''}
                    onChange={(e) => setFieldValue('data.modelNodeDataForm.point.x', sanitiseNumber(e.target.value))}
                    onBlur={handleBlur}
                    error={helpers.hasError('data.modelNodeDataForm.point.x')}
                    helperText={helpers.getErrorHelpText('data.modelNodeDataForm.point.x')}
                  />
                </Box>
                <Box sx={{ pt: 2 }}>
                  <TextField
                    color="secondary"
                    fullWidth
                    type="number"
                    inputMode="decimal"
                    id="data.modelNodeDataForm.point.y"
                    name="data.modelNodeDataForm.point.y"
                    label={i18n._('Y Coordinate')}
                    value={values.data.modelNodeDataForm.point.y ?? ''}
                    onChange={(e) => setFieldValue('data.modelNodeDataForm.point.y', sanitiseNumber(e.target.value))}
                    onBlur={handleBlur}
                    error={helpers.hasError('data.modelNodeDataForm.point.y')}
                    helperText={helpers.getErrorHelpText('data.modelNodeDataForm.point.y')}
                  />
                </Box>
                <Box sx={{ pt: 2 }}>
                  <TextField
                    color="secondary"
                    fullWidth
                    type="number"
                    inputMode="decimal"
                    id="data.modelNodeDataForm.point.z"
                    name="data.modelNodeDataForm.point.z"
                    label={i18n._('Z Coordinate')}
                    value={values.data.modelNodeDataForm.point.z ?? ''}
                    onChange={(e) => setFieldValue('data.modelNodeDataForm.point.z', sanitiseNumber(e.target.value))}
                    onBlur={handleBlur}
                    error={helpers.hasError('data.modelNodeDataForm.point.z')}
                    helperText={helpers.getErrorHelpText('data.modelNodeDataForm.point.z')}
                  />
                </Box>
              </>
          ) : (
            <>
              <ProjectAuthorise permission={Permission.ManageNodeData} module={ProjectModule.ManuallyAddedNodes}>
                <Box sx={{ pt: 2 }}>
                  <TextField
                    color="secondary"
                    fullWidth
                    type="number"
                    inputMode="decimal"
                    id="data.modelNodeDataForm.point.x"
                    name="data.modelNodeDataForm.point.x"
                    label={i18n._('X Coordinate')}
                    value={values.data.modelNodeDataForm.point.x ?? ''}
                    onChange={(e) => setFieldValue('data.modelNodeDataForm.point.x', sanitiseNumber(e.target.value))}
                    onBlur={handleBlur}
                    error={helpers.hasError('data.modelNodeDataForm.point.x')}
                    helperText={helpers.getErrorHelpText('data.modelNodeDataForm.point.x')}
                  />
                </Box>
              </ProjectAuthorise>
              <ProjectAuthorise permission={Permission.ManageNodeData} module={ProjectModule.ManuallyAddedNodes}>
                <Box sx={{ pt: 2 }}>
                  <TextField
                    color="secondary"
                    fullWidth
                    type="number"
                    inputMode="decimal"
                    id="data.modelNodeDataForm.point.y"
                    name="data.modelNodeDataForm.point.y"
                    label={i18n._('Y Coordinate')}
                    value={values.data.modelNodeDataForm.point.y ?? ''}
                    onChange={(e) => setFieldValue('data.modelNodeDataForm.point.y', sanitiseNumber(e.target.value))}
                    onBlur={handleBlur}
                    error={helpers.hasError('data.modelNodeDataForm.point.y')}
                    helperText={helpers.getErrorHelpText('data.modelNodeDataForm.point.y')}
                  />
                </Box>
              </ProjectAuthorise>
              <ProjectAuthorise permission={Permission.ManageNodeData} module={ProjectModule.ManuallyAddedNodes}>
                <Box sx={{ pt: 2 }}>
                  <TextField
                    color="secondary"
                    fullWidth
                    type="number"
                    inputMode="decimal"
                    id="data.modelNodeDataForm.point.z"
                    name="data.modelNodeDataForm.point.z"
                    label={i18n._('Z Coordinate')}
                    value={values.data.modelNodeDataForm.point.z ?? ''}
                    onChange={(e) => setFieldValue('data.modelNodeDataForm.point.z', sanitiseNumber(e.target.value))}
                    onBlur={handleBlur}
                    error={helpers.hasError('data.modelNodeDataForm.point.z')}
                    helperText={helpers.getErrorHelpText('data.modelNodeDataForm.point.z')}
                  />
                </Box>
              </ProjectAuthorise>
            </>
          )}
            {/* Level & Area */}
            <ProjectAuthorise permission={Permission.ManageNodeData}>
              <Box sx={{ pt: 2 }}>
                <TextField
                  color="secondary"
                  fullWidth
                  id="data.modelNodeDataForm.level"
                  name="data.modelNodeDataForm.level"
                  label={i18n._('Level')}
                  value={values.data.modelNodeDataForm.level ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={helpers.hasError('data.modelNodeDataForm.level')}
                  helperText={helpers.getErrorHelpText('data.modelNodeDataForm.level')}
                />
              </Box>
            </ProjectAuthorise>

            <ProjectAuthorise permission={Permission.ManageNodeData}>
              <Box sx={{ pt: 2 }}>
                <TextField
                  color="secondary"
                  fullWidth
                  id="data.modelNodeDataForm.area"
                  name="data.modelNodeDataForm.area"
                  label={i18n._('Area')}
                  value={values.data.modelNodeDataForm.area ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={helpers.hasError('data.modelNodeDataForm.area')}
                  helperText={helpers.getErrorHelpText('data.modelNodeDataForm.area')}
                />
              </Box>
            </ProjectAuthorise>

            {/* A flow loop is a length of pipe coiled into an edge, so it is edited per spool
                (Bulk Edit > Flow Loop Lengths), not here on the node. */}
            <ProjectAuthorise permission={Permission.ManageNodeData}>
              <Box sx={{ pt: 2 }}>
                <UnitSystemNumberField
                  color="secondary"
                  fullWidth
                  type="number"
                  unitSystem={model.project.unitSystemPreference}
                  unitInfo={unitLabels[BasicDisplayUnits.PressureUCS]}
                  decimalPlaces={1}
                  error={helpers.hasError('data.modelNodeDataForm.ruptureDiskPressure')}
                  helperText={helpers.getErrorHelpText('data.modelNodeDataForm.ruptureDiskPressure')}
                  id="data.modelNodeDataForm.ruptureDiskPressure"
                  name="data.modelNodeDataForm.ruptureDiskPressure"
                  label={i18n._('Rupture Disk Pressure')}
                  value={values.data.modelNodeDataForm.ruptureDiskPressure ?? 0}
                  onChange={(e) => setFieldValue('data.modelNodeDataForm.ruptureDiskPressure', sanitiseNumber(e.target.value))}
                  onBlur={handleBlur}
                  customUnitFields={model.project.customUnitFields}
                />
              </Box>
              {/* Choke station: a fixed head loss in metres of slurry, dissipated at this node */}
              <Box sx={{ pt: 2 }}>
                <UnitSystemNumberField
                  color="secondary"
                  fullWidth
                  type="number"
                  unitSystem={model.project.unitSystemPreference}
                  unitInfo={unitLabels[BasicDisplayUnits.Distance]}
                  decimalPlaces={1}
                  error={helpers.hasError('data.modelNodeDataForm.chokeHead')}
                  helperText={helpers.getErrorHelpText('data.modelNodeDataForm.chokeHead')}
                  id="data.modelNodeDataForm.chokeHead"
                  name="data.modelNodeDataForm.chokeHead"
                  label={i18n._('Choke Station Head')}
                  value={values.data.modelNodeDataForm.chokeHead ?? 0}
                  onChange={(e) => setFieldValue('data.modelNodeDataForm.chokeHead', sanitiseNumber(e.target.value))}
                  onBlur={handleBlur}
                  customUnitFields={model.project.customUnitFields}
                />
              </Box>
              <Box sx={{ pt: 2 }}>
                <TextField
                  color="secondary"
                  fullWidth
                  id="data.modelNodeDataForm.pit"
                  name="data.modelNodeDataForm.pit"
                  label={i18n._('Pressure Transmitter Name')}
                  value={values.data.modelNodeDataForm.pit ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={helpers.hasError('data.modelNodeDataForm.pit')}
                  helperText={helpers.getErrorHelpText('data.modelNodeDataForm.pit')}
                  disabled={hasStopeData}
                />
              </Box>
            </ProjectAuthorise>
            {canEditCoordinates && (
            <>
              <Box sx={{ pt: 2 }}>
                <UnitSystemNumberField
                  color="secondary"
                  fullWidth
                  type="number"
                  unitSystem={model.project.unitSystemPreference}
                  unitInfo={unitLabels[BasicDisplayUnits.PressureUCS]}
                  decimalPlaces={1}
                  error={helpers.hasError('data.modelNodeDataForm.boosterPressure')}
                  helperText={helpers.getErrorHelpText('data.modelNodeDataForm.boosterPressure')}
                  id="data.modelNodeDataForm.boosterPressure"
                  name="data.modelNodeDataForm.boosterPressure"
                  label={i18n._('Booster Pump Pressure')}
                  value={pumpPressureBarToKpa(values.data.modelNodeDataForm.boosterPressure ?? 0)}
                  onChange={(e) => setFieldValue('data.modelNodeDataForm.boosterPressure', pumpPressureKpaToBar(sanitiseNumber(e.target.value) ?? 0))}
                  onBlur={handleBlur}
                  customUnitFields={model.project.customUnitFields}
                />
              </Box>
              <Box
                sx={{
                  pt: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle1"><Trans id="Is Backfill Plant">Is Backfill Plant</Trans></Typography>
                <Checkbox
                  checked={values.data.modelNodeDataForm.isPlantNode ?? false}
                  onChange={(event) => {
                    setFieldValue('data.modelNodeDataForm.isPlantNode', event.target.checked);
                  }}
                  color="secondary"
                />
              </Box>
            </>
          )}
          </>
          )}
          {activeTab === 1 && (
          <>
            {/* Add/Undo stope */}
            <Box sx={{ pt: 2 }}>
              <Button
                color="secondary"
                fullWidth
                variant={
                isNewStope || selectedStope ? 'contained' : 'outlined'
              }
                size="large"
                disabled={values.data.modelNodeDataForm.isPit}
                onClick={() => removeStopeData(true)}
              >
                {isNewStope || selectedStope
                ? i18n._('Undo Stope')
                : i18n._('Add New Stope')}
              </Button>
            </Box>

            {/* STOPE MINI FORM WITH PIPE ARRAYS */}
            {(isNewStope || selectedStope) && (
            <>
              {/* Stope name */}
              <Box sx={{ pt: 2 }}>
                <TextField
                  color="secondary"
                  fullWidth
                  id="data.modelNodeDataForm.stopeData[0].stopeName"
                  name="data.modelNodeDataForm.stopeData[0].stopeName"
                  label={i18n._('Stope Name')}
                  value={
                    values.data.modelNodeDataForm.stopeData[0]
                      .stopeName ?? ''
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={helpers.hasError('data.modelNodeDataForm.stopeData[0].stopeName')}
                  helperText={helpers.getErrorHelpText('data.modelNodeDataForm.stopeData[0].stopeName')}
                  required
                />
              </Box>

              {/* PIPE ARRAYS – multiple rows */}
              <Box sx={{ pt: 2 }}>
                <Typography variant="subtitle1">
                  <Trans id="Stope Pipe Arrays">Stope Pipe Arrays</Trans>
                </Typography>

                {currentPipeArrays.map(
                  (pipeRow: StopePipeArray, index: number) => {
                    const pipeRowKey = pipeRow.pipeId?.value || `new-${index}`;
                    return (
                      <Box
                        key={pipeRowKey}
                        sx={{
                          mt: 2,
                          p: 1,
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          <Trans id="Pipe Row">Pipe Row</Trans>
                          {' '}
                          {index + 1}
                        </Typography>

                        {/* Pipe type per row */}
                        <PipeTypeSelectField
                          pipeTypes={model.pipes}
                          value={pipeRow.pipeId?.value ?? ''}
                          unitSystem={model.project.unitSystemPreference}
                          onChange={(pipeIdValue) => setFieldValue(`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeId`, { value: pipeIdValue } as PipeId)}
                          label={i18n._('Pipe Type')}
                          required
                        />

                        {/* Vertical length */}
                        <Box sx={{ pt: 2 }}>
                          <UnitSystemNumberField
                            color="secondary"
                            fullWidth
                            type="number"
                            unitSystem={model.project.unitSystemPreference}
                            unitInfo={unitLabels[BasicDisplayUnits.Distance]}
                            decimalPlaces={1}
                            error={helpers.hasError(`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeVerticalLength`)}
                            helperText={helpers.getErrorHelpText(`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeVerticalLength`)}
                            id={`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeVerticalLength`}
                            name={`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeVerticalLength`}
                            label={i18n._('Stope Pipe Vertical Length')}
                            value={pipeRow.pipeVerticalLength ?? ''}
                            onChange={(e) => setFieldValue(`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeVerticalLength`, sanitiseNumber(e.target.value))}
                            onBlur={handleBlur}
                            customUnitFields={model.project.customUnitFields}
                          />
                        </Box>

                        {/* Horizontal length */}
                        <Box sx={{ pt: 2 }}>
                          <UnitSystemNumberField
                            color="secondary"
                            fullWidth
                            type="number"
                            unitSystem={model.project.unitSystemPreference}
                            unitInfo={unitLabels[BasicDisplayUnits.Distance]}
                            decimalPlaces={1}
                            error={helpers.hasError(`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeHorizontalLength`)}
                            helperText={helpers.getErrorHelpText(`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeHorizontalLength`)}
                            id={`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeHorizontalLength`}
                            name={`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeHorizontalLength`}
                            value={pipeRow.pipeHorizontalLength ?? ''}
                            label={i18n._('Stope Pipe Horizontal Length')}
                            onChange={(e) => setFieldValue(`data.modelNodeDataForm.stopeData[0].pipeArrays[${index}].pipeHorizontalLength`, sanitiseNumber(e.target.value))}
                            onBlur={handleBlur}
                            customUnitFields={model.project.customUnitFields}
                          />
                        </Box>

                        {/* Remove row button */}
                        <Box sx={{ pt: 1, textAlign: 'right' }}>
                          <Button
                            color="secondary"
                            size="small"
                            disabled={currentPipeArrays.length <= 1}
                            onClick={(e) => {
                              e.preventDefault();
                              handleRemovePipeRow(index);
                            }}
                          >
                            <Trans id="Remove Pipe Row">
                              Remove Pipe Row
                            </Trans>
                          </Button>
                        </Box>
                      </Box>
                    );
                  },
                )}

                {/* Add row button */}
                <Box sx={{ pt: 2 }}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddPipeRow();
                    }}
                  >
                    <Trans id="Add Pipe Row">Add Pipe Row</Trans>
                  </Button>
                </Box>
              </Box>

              {/* Stope volume */}
              <Box sx={{ pt: 2 }}>
                <UnitSystemNumberField
                  color="secondary"
                  fullWidth
                  type="number"
                  unitSystem={model.project.unitSystemPreference}
                  unitInfo={unitLabels[BasicDisplayUnits.Volume]}
                  decimalPlaces={1}
                  error={helpers.hasError('data.modelNodeDataForm.stopeData[0].volume')}
                  helperText={helpers.getErrorHelpText('data.modelNodeDataForm.stopeData[0].volume')}
                  id="data.modelNodeDataForm.stopeData[0].volume"
                  name="data.modelNodeDataForm.stopeData[0].volume"
                  label={i18n._('Stope Volume')}
                  required
                  value={values.data.modelNodeDataForm.stopeData[0].volume}
                  onChange={(e) => setFieldValue('data.modelNodeDataForm.stopeData[0].volume', sanitiseNumber(e.target.value))}
                  onBlur={(e) => {
                    markFieldTouched('data.modelNodeDataForm.stopeData[0].volume');
                    handleBlur(e);
                  }}
                  customUnitFields={model.project.customUnitFields}
                />

              </Box>

              {/* Pour preset group */}
              <Box sx={{ pt: 2 }}>
                {model.project.entityId && (
                  <PourPresetGroupField
                    projectId={model.project.entityId}
                    pourPresetGroupId={values.data.modelNodeDataForm.stopeData[0].pourPresetGroupId}
                    onChange={(pourPresetGroup) => setFieldValue('data.modelNodeDataForm.stopeData[0].pourPresetGroupId', pourPresetGroup?.entityId)}
                  />
                )}
              </Box>

              {/* Start / End dates */}
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <Box sx={{ pt: 2 }}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    label={i18n._('Start Date')}
                    value={
                      values.data.modelNodeDataForm.stopeData[0]?.startDate
                        ? LocalDateToDayJs(values.data.modelNodeDataForm.stopeData[0].startDate as LocalDate)
                        : null
                    }
                    onChange={(date) => setFieldValue(
                      'data.modelNodeDataForm.stopeData[0].startDate',
                      date ? dayJsToLocalDate(date) : undefined,
                    )}
                    slotProps={{ textField: { color: 'secondary', fullWidth: true } }}
                  />
                </Box>
                <Box sx={{ pt: 2 }}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    label={i18n._('End Date')}
                    value={
                      values.data.modelNodeDataForm.stopeData[0]?.endDate
                        ? LocalDateToDayJs(values.data.modelNodeDataForm.stopeData[0].endDate as LocalDate)
                        : null
                    }
                    onChange={(date) => setFieldValue(
                      'data.modelNodeDataForm.stopeData[0].endDate',
                      date ? dayJsToLocalDate(date) : undefined,
                    )}
                    slotProps={{ textField: { color: 'secondary', fullWidth: true } }}
                  />
                </Box>
              </LocalizationProvider>
            </>
          )}

            {/* Stope list */}
            <Box sx={{ mt: 2 }}>
              {stopeDataList?.map((listData, index) => (
                <Box key={listData.stopeId ?? index}>
                  <StopeTitleCard
                    stopeId={listData?.stopeId ?? ''}
                    stopeName={listData?.stopeName ?? ''}
                    hasIncompletePours={!!overview?.find((m) => m.stopeIdentifier.stopeId === listData.stopeId)?.nextPourDate}
                    hasGeometry={!!listData.stopeGeometryFile}
                    stopeComplete={!!overview?.find((m) => m.stopeIdentifier.stopeId === listData.stopeId)?.isComplete}
                    completedVolume={overview?.find((m) => m.stopeIdentifier.stopeId === listData.stopeId)?.completedVolume ?? 0}
                    plannedVolume={overview?.find((m) => m.stopeIdentifier.stopeId === listData.stopeId)?.plannedVolume ?? 0}
                    volume={listData?.volume}
                    unitSystemPreference={model.project.unitSystemPreference}
                    customUnitFields={model.project.customUnitFields ?? []}
                    nodeId={nodeId ?? ''}
                    model={model}
                    onStopeEdit={() => handleEdit(listData.stopeId)}
                    onDeleteSuccess={(deletedNodeId) => handleDelete(deletedNodeId, listData.stopeId)}
                  />
                </Box>
            ))}
            </Box>

          </>
          )}
          {/* Form actions */}
          <Box sx={{ mt: 2 }}>
            <FormikActions
              formik={formik}
              mutation={mutation}
              submitText={i18n._('Create')}
              right={['reset', submitButton as [string, JSX.Element]]}
            />
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

export default NodeForm;
